import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  type ReportingV2Grouping,
  selectReportingV2Filters,
  setReportingV2Filters,
} from '@/store/slices/reportingV2/reportingV2Slice';
import { useEffect } from 'react';
import ReportingV2View from './components/ReportingV2View';
import { useReportingPermissions } from './hooks/useReportingPermissions';
import { useReportingV2 } from './hooks/useReportingV2';

/**
 * #4442/#4623 — the slice's initial `grouping: 'dsp'` default (chosen for
 * coverage, see reportingV2Slice.ts) assumes Platform is visible. A user
 * without that permission must not request data grouped by a dimension they
 * can't see or select, not even for one render.
 */
export const needsGroupingCorrection = (canViewPlatform: boolean, grouping: ReportingV2Grouping): boolean =>
  !canViewPlatform && grouping === 'dsp';

/**
 * Reporting 2.0 — canonical, DSP-agnostic reporting backed by ribeye-api's
 * `/reporting-v2/report`. Loads every widget on mount and whenever filters
 * change (loadAll is filter-dependent).
 */
const ReportingV2 = () => {
  const dispatch = useAppDispatch();
  const { loadAll } = useReportingV2();
  const { grouping } = useAppSelector(selectReportingV2Filters);
  const { canViewPlatform } = useReportingPermissions();

  /**
   * One effect, not two. `loadAll` closes over `filters` (including
   * `grouping`) from this same render — if the restriction correction lived
   * in a separate effect, React would batch both same-commit effects and
   * `loadAll` could still fire with the stale, restricted 'dsp' grouping
   * before the correction's re-render landed (flank#4650 CodeRabbit finding).
   * Gating the `loadAll` call behind the same check that issues the
   * correction makes that request structurally impossible: either this
   * render corrects `grouping` and returns without loading, or `grouping`
   * is already valid and `loadAll` runs.
   */
  useEffect(() => {
    if (needsGroupingCorrection(canViewPlatform, grouping)) {
      dispatch(setReportingV2Filters({ grouping: 'all' }));
      return;
    }
    loadAll();
  }, [canViewPlatform, grouping, dispatch, loadAll]);

  // Page padding/background now live on ReportingV2View's own root (the
  // sticky header needs to be a direct child of it, not of an outer wrapper
  // — see ReportingV2View.tsx).
  return <ReportingV2View />;
};

export default ReportingV2;
